import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { AuthApiService } from '../services/auth.api.service';
import { LoginResultVO } from '../domain/vo/LoginResultVO';

const initialLocation = window.location.pathname;

interface UserStoreState {
  token: string;
  refreshToken: string;
  uuid: string;
  userId: string;
  email: string;
  roles: string[];
  authorities: string[];
}

const emptyUser: UserStoreState = {
  token: '',
  refreshToken: '',
  uuid: '',
  userId: '',
  email: '',
  roles: [],
  authorities: [],
};

const useUserStore = create<UserStoreState>()(() => ({ ...emptyUser }));

function claimsOf(token: string): Pick<UserStoreState, 'uuid' | 'userId' | 'email' | 'roles' | 'authorities'> {
  const decoded: any = jwtDecode(token);
  return {
    uuid: decoded.uuid,
    userId: decoded.id,
    email: decoded.email,
    roles: decoded.roles,
    authorities: decoded.authorities,
  };
}

export function getInitialLocation() {
  return initialLocation;
}

async function refreshAccessToken(): Promise<LoginResultVO> {
  delete axios.defaults.headers.common['Authorization']; // remove Authorization for refresh-token-generation
  return await AuthApiService.tryRefreshToken();
}

let isRefreshing = false;
let refreshQueue: ((token: string) => void)[] = [];

// Function to add new subscribers waiting for the token refresh
function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshQueue.push(cb);
}

// Function to handle the token refresh
async function onTokenRefreshed(token: string) {
  isRefreshing = false;
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

export interface UserState {
  readonly isPresent: boolean;
  readonly canAskForRefreshToken: boolean;
  readonly token: string;
  readonly refreshToken: string;
  readonly uuid: string;
  readonly userId: string;
  readonly email: string;
  readonly roles: readonly string[];
  readonly authorities: readonly string[];
  setUser: (token: string, refreshToken: string) => void;
  logout: () => void;
}

// Registered once, when this module is first imported. It used to live inside useUserState, which meant every render
// of every component that reads the user added another copy: on a 401 each one refreshed and each one replayed the
// original request, so a POST that outlived its five-minute token was sent as many times as the page had rendered.
axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;
    const apiUrl = import.meta.env.VITE_API_URL;
    const isUnauthorized = error.response?.status === 401;

    if (isUnauthorized && originalRequest.url !== apiUrl + '/api/auth/refresh-token') {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshTokenResponse = await refreshAccessToken();
          axios.defaults.headers.common['Authorization'] = `Bearer ${refreshTokenResponse.token}`;
          useUserStore.setState({
            ...claimsOf(refreshTokenResponse.token),
            refreshToken: refreshTokenResponse.refreshToken,
            token: refreshTokenResponse.token,
          });
          await onTokenRefreshed(refreshTokenResponse.token);
          originalRequest.headers['Authorization'] = `Bearer ${refreshTokenResponse.token}`;
          return await axios(originalRequest);
        } catch (refreshError: any) {
          isRefreshing = false;
          // Handle the error (note this can be error from original call, not from refresh
          if (refreshError?.isAxiosError) {
            const axiosError = refreshError as AxiosError;
            if (axiosError.response?.status === 401) {
              // log the user out
              window.location.reload();
            }
          }
          throw refreshError;
        }
      }

      return new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
    }
    return Promise.reject(error);
  },
);

export function useUserState(): UserState {
  const state = useUserStore();

  return {
    get isPresent() {
      return state.token.length !== 0;
    },
    get canAskForRefreshToken() {
      return state.refreshToken !== '-';
    },
    get token() {
      return state.token;
    },
    get refreshToken() {
      return state.refreshToken;
    },
    get uuid() {
      return state.uuid;
    },
    get userId() {
      return state.userId;
    },
    get email() {
      return state.email;
    },
    get roles() {
      return state.roles;
    },
    get authorities() {
      return state.authorities;
    },
    setUser(token: string, refreshToken: string) {
      if (token.length === 0) {
        useUserStore.setState({ ...emptyUser, refreshToken });
        axios.defaults.headers.common = {};
      } else {
        useUserStore.setState({ ...claimsOf(token), refreshToken, token });
        axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
      }
    },
    logout() {
      useUserStore.setState({ ...emptyUser });
      axios.defaults.headers.common = {};
    },
  };
}
